package fr.manyverse.app.presentation.components.player_screen.character.choice

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.util.toDisplayNameFromFileName
import fr.manyverse.app.presentation.components.clearFocusOnTap
import fr.manyverse.app.presentation.components.ImagePickerButton
import fr.manyverse.app.presentation.components.rememberImageSelection
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.TableCharacterViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.character_name
import manyverse.shared.generated.resources.character_name_hint
import manyverse.shared.generated.resources.character_story
import manyverse.shared.generated.resources.character_story_hint
import manyverse.shared.generated.resources.create
import manyverse.shared.generated.resources.create_character
import manyverse.shared.generated.resources.image_optional
import org.jetbrains.compose.resources.stringResource

@Composable
fun CreateCharacterInTableDialog(
    viewModel: TableCharacterViewModel,
    onDismiss: () -> Unit
) {
    var name by remember { mutableStateOf("") }
    var story by remember { mutableStateOf("") }
    val imageSelection = rememberImageSelection { image ->
        if (name.isBlank()) name = image.fileName.toDisplayNameFromFileName()
    }

    val assignState by viewModel.assignState.collectAsStateWithLifecycle()
    val isLoading = assignState is AsyncUiState.Loading

    LaunchedEffect(assignState) {
        if (assignState is AsyncUiState.Success) {
            viewModel.resetAssignState()
            onDismiss()
        }
    }

    AlertDialog(
        properties = DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false),
        modifier = Modifier.clearFocusOnTap(),
        onDismissRequest = { if (!isLoading) onDismiss() },
        title = { Text(stringResource(Res.string.create_character)) },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        ImagePickerButton(
                            currentImageUrl = null,
                            selectedImageData = imageSelection.selected,
                            onPickImage = imageSelection::pick,
                            onRemoveImage = imageSelection::clear,
                            enabled = !isLoading
                        )
                        Text(
                            text = stringResource(Res.string.image_optional),
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }

                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text(stringResource(Res.string.character_name)) },
                    placeholder = { Text(stringResource(Res.string.character_name_hint)) },
                    singleLine = true,
                    enabled = !isLoading,
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    modifier = Modifier.fillMaxWidth()
                )
                OutlinedTextField(
                    value = story,
                    onValueChange = { story = it },
                    label = { Text(stringResource(Res.string.character_story)) },
                    placeholder = { Text(stringResource(Res.string.character_story_hint)) },
                    minLines = 3,
                    maxLines = 6,
                    enabled = !isLoading,
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
                    modifier = Modifier.fillMaxWidth()
                )

                if (assignState is AsyncUiState.Error) {
                    Text(
                        text = (assignState as AsyncUiState.Error).message,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
                if (isLoading) {
                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                }
            }
        },
        confirmButton = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                TextButton(onClick = onDismiss, enabled = !isLoading) {
                    Text(stringResource(Res.string.cancel))
                }
                Button(
                    onClick = { viewModel.createAndAssignCharacter(name, story, imageSelection.selected) },
                    enabled = name.isNotBlank() && !isLoading
                ) {
                    Text(stringResource(Res.string.create))
                }
            }
        }
    )
}
